import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import Button from "../../../../../components/button/button";
import styles from "./stripe-form.module.scss";
import {
  AddressElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import CONFIG from "../../../../../core/consts/config";
import { useDI } from "../../../../../core/di";
import useStripeCallback from "./use-stripe-callback";
import { Form } from "../type";
import Event from "../../../../../core/consts/analytics-events";
import useGlobalFontUrl from "./use-global-font-url";

export function InnerForm(props: Form) {
  const {
    introText,
    introComponent,
    onSuccess,
    onError,
    status,
    subscriptionId,
  } = props;
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();

  const { nexarApi, analytics, logger } = useDI();

  const success = useStripeCallback(onSuccess, onError);
  if (success) {
    return <></>;
  }

  const handleSubmit = async (event: any) => {
    // Stop the form submission native behavior
    event.preventDefault();

    if (!elements || !stripe) {
      return;
    }

    // Animate the loader
    setLoading(true);

    // Validate the form
    const { error } = await elements.submit();
    if (error) {
      // Stop loader animation
      setLoading(false);
      // focus on the error
      if (error.message?.match(/card/i)) {
        elements.getElement("payment")?.focus();
      } else {
        elements.getElement("address")?.focus();
      }
      return;
    }

    try {
      const setupIntent = await nexarApi.createSubscriptionSetupIntent({
        subscriptionId: subscriptionId,
      });

      const result = await stripe.confirmSetup({
        elements,
        clientSecret: setupIntent.clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
      });
      if (result.error) {
        const mixpanelEvent =
          status === "ACTIVE"
            ? Event.ConnectEditFailed
            : Event.ConnectActivateFailed;

        analytics?.track(mixpanelEvent, {
          subscriptionId,
          error: result.error,
        });

        logger?.Error(new Error("Failed to handle subscription action"), {
          subscription: {
            id: subscriptionId,
            status: status,
            formValues: elements.getElement("address")?.getValue(),
          },
          error: result.error,
        });

        onError(result.error);
        // Stop loader animation
        setLoading(false);
        return;
      }
    } catch (e) {
      const mixpanelEvent =
        status === "ACTIVE"
          ? Event.ConnectEditFailed
          : Event.ConnectActivateFailed;

      analytics?.track(mixpanelEvent, {
        subscriptionId,
        error: e,
      });

      logger?.Error(new Error("Failed to handle subscription action"), {
        subscription: {
          id: subscriptionId,
          status: status,
          formValues: elements.getElement("address")?.getValue(),
        },
        error: e,
      });
      onError(e);
    } finally {
      // Stop loader animation
      setLoading(false);
    }
  };

  const intro = () => {
    return (
      <div className={styles.Intro}>
        {introText ? <p>{introText}</p> : introComponent}
      </div>
    );
  };

  const renderForm = () => {
    return (
      <React.Fragment>
        <div className={styles.Fields}>
          <AddressElement
            options={{
              mode: "billing",
              allowedCountries: ["US"],
              fields: {
                phone: "never",
              },
            }}
          />
          {/*<CardElement options={{*/}
          {/*    hidePostalCode:true,*/}
          {/*}}/>*/}
          <PaymentElement />
        </div>
        <footer className={styles.Footer}>{footer()}</footer>
      </React.Fragment>
    );
  };

  const footer = () => {
    return (
      <footer>
        <Button
          submit={true}
          loading={loading}
          data-testid={"subscription-form-submit"}
        >
          {props.btnText}
        </Button>
        <p>{props.footerParagraph}</p>
      </footer>
    );
  };

  return (
    <div className={styles.FormWrapper}>
      {intro()}
      <form action="#" onSubmit={handleSubmit}>
        <h2>{t("paymentDetails")}</h2>
        {renderForm()}
      </form>
    </div>
  );
}

function MyNexarStripeForm(props: Form) {
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const fontsSrc = useGlobalFontUrl();

  useEffect(() => {
    if (typeof CONFIG.STRIPE_PUBLIC_KEY != "undefined") {
      const stripePromise = loadStripe(CONFIG.STRIPE_PUBLIC_KEY);
      setStripePromise(stripePromise);
    }
  }, [CONFIG.STRIPE_PUBLIC_KEY]);

  return (
    <Elements
      stripe={stripePromise}
      options={{
        fonts: [
          {
            cssSrc: fontsSrc,
          },
        ],
        appearance: {
          theme: "flat",
          variables: {
            fontFamily: "Roobert, serif",
            colorTextPlaceholder: "transparent",
            borderRadius: "0",
            focusOutline: "0",
            focusBoxShadow: "none",
          },
          rules: {
            ".Label": {
              color: "rgb(113, 119, 130)",
              textTransform: "uppercase",
              fontSize: "11px",
              letterSpacing: "1.18px",
            },
            ".Input": {
              fontSize: "16px",
              fontWeight: "600",
              padding: "4px 0 5px",
              backgroundColor: "white",
              borderBottom: "1px solid #e4e5e7",
              transition: "border ease-out 150ms",
            },
            ".Input:hover": {
              outline: "0",
              borderBottom: "1px solid rgba(0, 0, 0, 0.87)",
            },
            ".Input:focus:hover": {
              outline: "0",
              borderBottom: "1px solid #7a4dff",
            },
            ".Input--invalid": {
              boxShadow: "none",
              borderBottom: "1px solid var(--colorDanger)",
            },
          },
          labels: "above",
        },
        setupFutureUsage: "off_session",
        currency: "usd",
        mode: "setup",
        // todo this determines if I use payment intent or payment method
        paymentMethodCreation: "manual",
      }}
    >
      <InnerForm {...props} />
    </Elements>
  );
}

export default MyNexarStripeForm;
